import React, { useState } from 'react';
import { 
    Box, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { 
    Print as PrintIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { formatNumberWithComma, formatCreatedAtDate } from '../../../utils/helpers';
import { companyInfo } from '../../../utils/dataInfo';

const PrintOrderReceipt = ({ orderData, onPrintComplete, servantName }) => {
    const [printError, setPrintError] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    // Helper function to get all unique servants from order items
    const getAllServants = () => {
        if (!orderData.club_order_items || !Array.isArray(orderData.club_order_items)) {
            return servantName || 'N/A';
        }
        
        const servantIds = [...new Set(orderData.club_order_items.map(item => item.servant_id))];
        // If we have multiple servants, we'll show them in the receipt
        return servantIds.length > 1 ? 'Multiple Servants' : servantName;
    };

    // Helper function to get servant name by ID (if we have multiple servants)
    const getServantNameById = (servantId) => {
        // You can pass an employee list prop if needed
        return servantName; // Fallback to the main servant name
    };

    const generateReceiptHTML = () => {
        const items = orderData.club_order_items || [];
        const servantDisplay = getAllServants();
        
        // Create items table HTML
        const itemsTableHtml = items.map(item => `
            <tr>
                <td>${item.stock_name || 'Item'}</td>
                <td>${item.quantity}</td>
                <td>${formatNumberWithComma(item.price)}</td>
                <td>${formatNumberWithComma(item.total_amount)}</td>
            </tr>
        `).join('');

        // Group items by servant for detailed view
        const itemsByServant = items.reduce((acc, item) => {
            const servantId = item.servant_id;
            if (!acc[servantId]) {
                acc[servantId] = {
                    items: []
                };
            }
            acc[servantId].items.push(item);
            return acc;
        }, {});

        // Create detailed items by servant HTML
        const detailedItemsHtml = Object.keys(itemsByServant).length > 1 ? 
            `<div style="margin-top: 10px; font-size: 11px;">
                <hr>
                <div style="font-weight: bold; margin-bottom: 5px;">Items by Servant:</div>
                ${Object.entries(itemsByServant).map(([servantId, servantData]) => `
                    <div style="margin-bottom: 8px;">
                        <div style="font-weight: bold; font-size: 10px;">Servant: ${getServantNameById(servantId)}</div>
                        ${servantData.items.map(item => `
                            <div style="margin-left: 10px; display: flex; justify-content: space-between;">
                                <span>${item.stock_name} x${item.quantity}</span>
                                <span>${formatNumberWithComma(item.total_amount)}</span>
                            </div>
                        `).join('')}
                    </div>
                `).join('')}
            </div>` : '';

        return `
        <!DOCTYPE html>
        <html>
            <head>
                <title>Receipt - ${orderData.order_number}</title>
                <style>
                    @page { size: auto; margin: 0; }
                    body { 
                        font-family: Arial, sans-serif; 
                        max-width: 80mm; 
                        margin: 0 auto; 
                        padding: 10px;
                        font-size: 12px;
                    }
                    .header { text-align: center; margin-bottom: 10px; }
                    .company-name { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
                    .company-info { font-size: 10px; margin: 2px 0; }
                    .title { text-align: center; font-size: 14px; font-weight: bold; margin: 10px 0; }
                    .info-row { display: flex; justify-content: space-between; margin: 5px 0; font-size: 11px; }
                    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin: 5px 0; }
                    .info-label { color: #555; font-size: 10px; }
                    .info-value { font-weight: bold; }
                    .items-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
                    .items-table th { 
                        text-align: left; 
                        border-bottom: 1px solid #000; 
                        padding: 5px 0;
                        font-size: 11px;
                    }
                    .items-table td { 
                        padding: 5px 0; 
                        border-bottom: 1px dotted #ccc;
                        font-size: 11px;
                    }
                    .items-table td:last-child, .items-table th:last-child { text-align: right; }
                    .total-row { 
                        font-weight: bold; 
                        margin-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 14px;
                    }
                    .payment-info {
                        margin-top: 10px;
                        padding: 5px;
                        background-color: #f5f5f5;
                        border-radius: 3px;
                    }
                    .footer { text-align: center; margin-top: 15px; font-size: 10px; }
                    .thank-you { text-align: center; margin: 10px 0; font-style: italic; font-size: 12px; }
                    .notes { 
                        margin-top: 10px; 
                        padding: 5px; 
                        border-top: 1px dashed #ccc;
                        font-size: 10px;
                        font-style: italic;
                    }
                    hr { border: none; border-top: 1px dashed #ccc; margin: 8px 0; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="company-name">${companyInfo.name}</div>
                    <div class="company-info">${companyInfo.address}</div>
                    <div class="company-info">Tel: ${companyInfo.phone}</div>
                    <div class="company-info">${companyInfo.email || ''}</div>
                </div>
                
                <div class="title">ORDER RECEIPT</div>
                
                <div class="info-row">
                    <span class="info-label">Order #:</span>
                    <span class="info-value">${orderData.order_number}</span>
                </div>
                
                <div class="info-row">
                    <span class="info-label">Date:</span>
                    <span class="info-value">${formatCreatedAtDate(orderData.created_at)}</span>
                </div>
                
                <div class="info-row">
                    <span class="info-label">Time:</span>
                    <span class="info-value">${new Date(orderData.created_at).toLocaleTimeString()}</span>
                </div>
                
                <hr>
                
                <div class="info-grid">
                    <div>
                        <div class="info-label">Customer</div>
                        <div class="info-value">${orderData.customer_id || 'Walk-in Customer'}</div>
                    </div>
                    <div>
                        <div class="info-label">Customer Type</div>
                        <div class="info-value">${orderData.customer_type?.name || 'N/A'}</div>
                    </div>
                </div>
                
                <div class="info-grid">
                    <div>
                        <div class="info-label">Servant</div>
                        <div class="info-value">${servantDisplay}</div>
                    </div>
                    <div>
                        <div class="info-label">Counter</div>
                        <div class="info-value">${orderData.counter?.name || 'N/A'}</div>
                    </div>
                </div>
                
                <hr>
                
                <table class="items-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsTableHtml}
                    </tbody>
                </table>
                
                ${detailedItemsHtml}
                
                <hr>
                
                <div class="total-row">
                    <span>TOTAL AMOUNT:</span>
                    <span>${formatNumberWithComma(orderData.total_amount)}</span>
                </div>
                
                <div class="payment-info">
                    <div class="info-row">
                        <span class="info-label">Status:</span>
                        <span style="color: ${orderData.order_status === 'completed' ? '#4caf50' : '#ff9800'}; font-weight: bold;">
                            ${orderData.order_status.toUpperCase()}
                        </span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Payment Method:</span>
                        <span style="font-weight: bold;">${orderData.payment_method.toUpperCase()}</span>
                    </div>
                    ${orderData.transaction_id ? `
                        <div class="info-row">
                            <span class="info-label">Transaction ID:</span>
                            <span style="font-family: monospace;">${orderData.transaction_id}</span>
                        </div>
                    ` : ''}
                </div>
                
                ${orderData.order_notes ? `
                    <div class="notes">
                        <strong>Notes:</strong> ${orderData.order_notes}
                    </div>
                ` : ''}
                
                ${orderData.payment_notes ? `
                    <div class="notes">
                        <strong>Payment Notes:</strong> ${orderData.payment_notes}
                    </div>
                ` : ''}
                
                <div class="footer">
                    <div>${companyInfo.receiptFooter || 'Thank you for your business!'}</div>
                </div>
                
                <div class="thank-you">THANK YOU!</div>
                
                <div style="text-align: center; font-size: 8px; margin-top: 10px; color: #999;">
                    ${new Date().toLocaleString()}
                </div>
            </body>
        </html>
        `;
    };

    const handlePrint = () => {
        setIsPrinting(true);
        setPrintError(false);

        try {
            const printContent = generateReceiptHTML();
            
            // Check if pop-ups are blocked
            const printWindow = window.open('', '_blank');
            
            if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
                // Pop-up was blocked
                setPrintError(true);
                setIsPrinting(false);
                return;
            }

            printWindow.document.write(printContent);
            printWindow.document.close();
            
            // Focus and print
            printWindow.focus();
            
            // Use a small timeout to ensure content is loaded
            setTimeout(() => {
                try {
                    printWindow.print();
                    
                    // Handle after print
                    printWindow.onafterprint = () => {
                        printWindow.close();
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    };
                    
                    // Fallback for browsers that don't support onafterprint
                    setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close();
                        }
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    }, 1000);
                    
                } catch (printErr) {
                    console.error('Print error:', printErr);
                    setPrintError(true);
                    setIsPrinting(false);
                }
            }, 500);
            
        } catch (error) {
            console.error('Print error:', error);
            setPrintError(true);
            setIsPrinting(false);
        }
    };

    const handleManualPrint = () => {
        // This function can be called from a button click
        handlePrint();
    };

    // Auto-print when component mounts, but handle popup blocker
    React.useEffect(() => {
        // Use a small delay to ensure component is fully mounted
        const timer = setTimeout(() => {
            handlePrint();
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // Error Dialog
    if (printError) {
        return (
            <Dialog open={printError} onClose={() => setPrintError(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <span>Print Error</span>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Pop-up blocker prevented the receipt from printing automatically.
                    </Alert>
                    <Typography variant="body2" paragraph>
                        Please click the button below to print manually, or check your browser settings to allow pop-ups for this site.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                            onClick={handleManualPrint}
                            size="large"
                        >
                            Print Receipt Manually
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setPrintError(false);
                            if (onPrintComplete) onPrintComplete();
                        }}
                        color="error"
                        startIcon={<CloseIcon />}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Loading state
    if (isPrinting) {
        return (
            <Dialog open={isPrinting}>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography>Preparing receipt for printing...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Box sx={{ display: 'none' }}>
            {/* This component doesn't render anything visible when not printing */}
        </Box>
    );
};

export default PrintOrderReceipt;